/**
 * Gamepad / joystick adapter for foot pedals.
 *
 * Supports two modes:
 * - independent: each button index maps to an action (Philips Footcontrol USB)
 * - combo: vPedal-style dual-button (btn0+btn1 = play, btn0 = rewind, btn1 = FF)
 *
 * @see https://www.vpedal.com/vpedal-api/
 */

/** @typedef {(event: { action: string, phase: 'down' | 'up' | 'change', raw?: string }) => void} PedalEventHandler */

const POLL_MS = 50;
const BUTTON_THRESHOLD = 0.5;
const MAX_SCAN_BUTTONS = 4;

function isPressed(pad, index) {
    const btn = pad?.buttons[index];
    return Boolean(btn?.pressed || (btn?.value ?? 0) >= BUTTON_THRESHOLD);
}

/**
 * vPedal combo mode: btn0+btn1 = play, btn0 = rewind, btn1 = fast forward.
 * @param {Gamepad} pad
 * @returns {string}
 */
function readComboAction(pad) {
    if (!pad) return "pause";

    const btn0 = isPressed(pad, 0);
    const btn1 = isPressed(pad, 1);

    if (btn0 && btn1) return "play";
    if (btn0) return "rewind";
    if (btn1) return "fastForward";
    return "pause";
}

/**
 * Independent mode: use configured button mapping.
 * If multiple buttons are pressed, prefer play over scrub actions.
 * @param {Gamepad} pad
 * @param {Record<string | number, string>} mapping
 * @returns {string}
 */
function readIndependentAction(pad, mapping) {
    if (!pad) return "pause";

    const pressed = [];
    const count = Math.min(pad.buttons.length, MAX_SCAN_BUTTONS);
    for (let i = 0; i < count; i++) {
        if (isPressed(pad, i)) {
            const action = mapping[i] ?? mapping[String(i)] ?? "none";
            if (action !== "none") {
                pressed.push(action);
            }
        }
    }

    if (pressed.includes("play")) return "play";
    if (pressed.includes("fastForward")) return "fastForward";
    if (pressed.includes("rewind")) return "rewind";
    if (pressed.includes("pause")) return "pause";
    return "pause";
}

/**
 * @param {{
 *   onPedalEvent: PedalEventHandler,
 *   getGamepadIndex?: () => number | null,
 *   getMode?: () => 'independent' | 'combo',
 *   getMapping?: () => Record<string | number, string>,
 * }} options
 */
export function createGamepadAdapter({
    onPedalEvent,
    getGamepadIndex,
    getMode = () => "independent",
    getMapping = () => ({}),
}) {
    /** @type {ReturnType<typeof setInterval> | undefined} */
    let pollId;
    /** @type {string} */
    let lastAction = "pause";

    function readAction(pad) {
        const mode = getMode ? getMode() : "independent";
        if (mode === "combo") {
            return readComboAction(pad);
        }
        return readIndependentAction(pad, getMapping ? getMapping() : {});
    }

    function poll() {
        if (!navigator.getGamepads) return;

        const pads = navigator.getGamepads();
        const preferred = getGamepadIndex ? getGamepadIndex() : null;
        let pad = null;

        if (preferred != null && pads[preferred]) {
            pad = pads[preferred];
        } else {
            pad = pads.find((p) => p) || null;
        }

        const action = readAction(pad);

        if (action === lastAction) return;

        if (lastAction !== "pause") {
            onPedalEvent({ action: lastAction, phase: "up", raw: lastAction });
        }

        lastAction = action;

        if (action !== "pause") {
            onPedalEvent({ action, phase: "down", raw: action });
        }
    }

    return {
        async start() {
            lastAction = "pause";
            pollId = setInterval(poll, POLL_MS);
        },

        async stop() {
            if (pollId) {
                clearInterval(pollId);
                pollId = undefined;
            }
            lastAction = "pause";
        },
    };
}

/**
 * @returns {{ index: number, id: string }[]}
 */
export function listGamepads() {
    if (!navigator.getGamepads) return [];
    const pads = navigator.getGamepads();
    const result = [];
    for (let i = 0; i < pads.length; i++) {
        const pad = pads[i];
        if (pad) {
            result.push({ index: i, id: pad.id });
        }
    }
    return result;
}
